import axios, { AxiosResponse } from 'axios';
import { ApiResponse, Component } from '../types';
import { createStandaloneToast } from '@chakra-ui/react';

const { toast } = createStandaloneToast();

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: '/api',
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`REQUEST: API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('ERROR: API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging and error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`SUCCESS: API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('ERROR: API Response Error:', error.response?.data || error.message);
    // Show toast
    toast({
      title: 'Fehler',
      description: error.response?.data?.message || error.message || 'Ein unbekannter Fehler ist aufgetreten.',
      status: 'error',
      duration: 6000,
      isClosable: true,
      position: 'top'
    });
    return Promise.reject(error);
  }
);

// API service class
class ApiService {
  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response: AxiosResponse<ApiResponse> = await apiClient.get('/health');
      return response.data.success;
    } catch (error) {
      console.error('ERROR: Health check failed:', error);
      return false;
    }
  }

  // Contact form service health check
  async contactFormHealthCheck(): Promise<boolean> {
    try {
      const response: AxiosResponse<ApiResponse> = await apiClient.get('/contact/health');
      return response.data.success;
    } catch (error) {
      console.error('ERROR: Contact form health check failed:', error);
      return false;
    }
  }

  // Fetch form configuration by ID
  async getFormConfig(formId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await apiClient.get(`/contact/${formId}`);
      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch form configuration');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'An error occurred while fetching form configuration');
    }
  }

  // Fetch components for a given category
  async getComponentsByCategory(categoryId: string): Promise<Component[]> {
    try {
      const response: AxiosResponse<ApiResponse<Component[]>> = await apiClient.get(`/contact/category/${categoryId}/components`);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch components');
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error(error.message || 'An error occurred while fetching components');
    }
  }

  // Submit dynamic form data
  async submitDynamicForm(formData: any, categoryId?: string, accountId?: string, contactFormId?: string): Promise<{ submissionId: string; message: string }> {
    try {
      const response: AxiosResponse<ApiResponse<{ submissionId: string; message: string }>> = await apiClient.post(
        '/contact/submit-form',
        { formData, categoryId, accountId, contactFormId }
      );

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to submit form');
      }
    } catch (error: any) {
      // Handle different types of errors
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // If backend returns a message
        if (errorData.message) {
          throw new Error(errorData.message);
        }
        
        // If backend returns an error
        if (errorData.error) {
          throw new Error(errorData.error);
        }
      }
      
      // Network or other errors
      if (error.message) {
        throw new Error(error.message);
      }
      
      throw new Error('An unexpected error occurred. Please try again later.');
    }
  }
}

const apiService = new ApiService();
export default apiService; 